import { request } from './api'

export interface UserSettings {
  id: string
  user_id: string
  theme_name: string | null // Changed from theme_id to theme_name
  theme_mode: string | null
  view_mode: string | null
  language: string | null
  preferences: Record<string, unknown> | null
  created_at: number
  updated_at: number
  effective_theme_name?: string | null
  effective_theme_mode?: string | null
  effective_language?: string | null
  can_override_theme?: boolean
  can_override_language?: boolean
  locked_by?: 'none' | 'platform' | 'tenant'
}

export interface UserSettingsUpdate {
  theme_name?: string | null // Changed from theme_id to theme_name
  theme_mode?: string | null
  view_mode?: string | null
  language?: string | null
  preferences?: Record<string, unknown> | null
}

export interface ThemePreference {
  theme_name: string // Changed from theme_id to theme_name
  theme_mode: string
}

export interface LanguagePreference {
  language: string
}

/** AI Workflows canvas UI theme (TAGP user settings; persisted in preferences.ai_workflows). */
export type AiWorkflowsUiTheme = 'standard' | 'atom-command'

export interface AiWorkflowsSettings {
  ui_theme: AiWorkflowsUiTheme
}

// ---------------------------------------------------------------------------
// Shared in-flight / TTL cache for getSettings(). Prevents triple-fetch when
// AuthContext, LanguageContext, and ThemeContext all call on the same tick.
// ---------------------------------------------------------------------------
let _settingsPromise: Promise<UserSettings> | null = null
let _settingsTimestamp = 0
const SETTINGS_CACHE_MS = 30_000

export async function getSettingsCached(): Promise<UserSettings> {
  const now = Date.now()
  if (_settingsPromise && now - _settingsTimestamp < SETTINGS_CACHE_MS) {
    return _settingsPromise
  }
  _settingsTimestamp = now
  _settingsPromise = request<UserSettings>('/settings').catch((err) => {
    _settingsPromise = null
    throw err
  })
  return _settingsPromise
}

export function invalidateSettingsCache(): void {
  _settingsPromise = null
  _settingsTimestamp = 0
}

/** Dispatched when platform/tenant admins change branding so contexts re-sync. */
export const BRANDING_SETTINGS_CHANGED_EVENT = 'tagp-branding-settings-changed'

export interface BrandingSettingsChangedDetail {
  previewThemeName?: string | null
  previewThemeMode?: 'light' | 'dark'
  previewLanguage?: string | null
}

export function notifyBrandingSettingsChanged(
  detail?: BrandingSettingsChangedDetail,
): void {
  invalidateSettingsCache()
  window.dispatchEvent(new CustomEvent(BRANDING_SETTINGS_CHANGED_EVENT, { detail }))
}

export const settingsApi = {
  getSettings: () => request<UserSettings>('/settings'),

  get: () => request<UserSettings>('/settings'),

  updateSettings: (data: UserSettingsUpdate) =>
    request<UserSettings>('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  update: (data: UserSettingsUpdate) =>
    request<UserSettings>('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getTheme: () => request<ThemePreference>('/settings/theme'),

  updateTheme: (themeName: string, themeMode: string) =>
    request<ThemePreference>('/settings/theme', {
      method: 'PUT',
      body: JSON.stringify({
        theme_name: themeName, // Changed from theme_id to theme_name
        theme_mode: themeMode,
      }),
    }),

  getLanguage: () => request<LanguagePreference>('/settings/language'),

  updateLanguage: (language: string) =>
    request<LanguagePreference>('/settings/language', {
      method: 'PUT',
      body: JSON.stringify({
        language,
      }),
    }),

  getAiWorkflowsSettings: () => request<AiWorkflowsSettings>('/settings/ai-workflows'),

  updateAiWorkflowsSettings: (data: { ui_theme: AiWorkflowsUiTheme }) =>
    request<AiWorkflowsSettings>('/settings/ai-workflows', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
}
